import React, { useState, useEffect, useRef, useCallback } from 'react';
import { debounce, noop } from 'lodash';
import { HotKeys } from 'react-hotkeys';
import onClickOutside from 'react-onclickoutside';

import { SContainer, SInput, SIcon } from './styled';

interface Props {
  isActiveFromProps?: boolean;
  placeholder?: string;
  isReversed?: boolean;
  itemsCount?: number;
  shouldNotFocus?: boolean;
  getItems?: (arg: string) => void;
  onToggleStatus?: (arg: boolean) => void;
  onEnter?: () => void;
}

const HOTKEYS_MAP = {
  enter: 'enter',
  leave: 'esc',
};

const Search = ({
  isActiveFromProps = false,
  placeholder = 'Search',
  isReversed = false,
  itemsCount,
  shouldNotFocus = false,
  getItems,
  onToggleStatus = noop,
  onEnter = noop,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isActive, setIsActive] = useState(false);
  const isDisabled = useCallback(() => !itemsCount && !searchTerm, [
    itemsCount,
    searchTerm,
  ]);

  const getItemsDebounce = useRef(debounce(getItems!, 300));

  useEffect(() => {
    if (isActive !== isActiveFromProps && !isDisabled()) {
      setIsActive(isActiveFromProps);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isActiveFromProps]);

  useEffect(() => {
    if (searchTerm === '') {
      onToggleStatus(false);
    }

    if (isActive && !shouldNotFocus && (inputRef && inputRef.current)) {
      inputRef.current.focus();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isActive]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchTerm(value);

    await getItemsDebounce.current(value);
  };

  const handleEscape = () => {
    if (searchTerm === '') {
      setIsActive(false);
    } else {
      setSearchTerm('');
    }
  };

  const handleFocus = () => {
    setIsActive(true);
  };

  // @ts-ignore
  Search.handleClickOutside = () => {
    if (searchTerm === '') {
      setIsActive(false);
    }
  };

  const handleHotKeys = {
    enter: () => onEnter(),
    leave: handleEscape,
  };

  return (
    <SContainer data-test="search-container" isReversed={isReversed}>
      <HotKeys handlers={handleHotKeys} keyMap={HOTKEYS_MAP}>
        <SInput
          ref={inputRef}
          autoComplete="off"
          data-test="search-input"
          isActive={isActive}
          name="search"
          placeholder={placeholder}
          type="text"
          value={searchTerm}
          onChange={handleChange}
        />
      </HotKeys>
      <SIcon
        className="icon-search"
        data-test="search"
        isDisabled={isDisabled()}
        onClick={handleFocus}
      />
    </SContainer>
  );
};

const clickOutsideConfig = {
  // @ts-ignore
  handleClickOutside: () => Search.handleClickOutside,
};

export default onClickOutside(Search, clickOutsideConfig);
